import { Icon, IconButton, Menu, Tooltip } from '@elipssolution/harfang';
import { mdiDeleteOutline } from '@mdi/js';
import { ListItemIcon, ListItemText, MenuItem, Stack, styled } from '@mui/material';
import clsx from 'clsx';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';

import useFormat from '../../../hooks/useFormat';
import { SupportFormType } from '../../types/support';
import { formatFileSize } from '../../utils/file';

const UploadedImagesWrapper = styled('div')(({ theme: { shape, spacing } }) => ({
	flex: 1,
	flexWrap: 'wrap',
	display: 'flex',
	alignItems: 'center',
	marginTop: spacing(1),
	gap: spacing(1),

	'& > div:first-of-type > span': {
		borderRadius: shape.borderRadius,
	},
}));

const ImageContainer = styled('div')(({ theme: { palette, shape, transitions } }) => ({
	position: 'relative',
	display: 'flex',
	justifyContent: 'center',
	overflow: 'hidden',
	borderRadius: shape.borderRadius,
	border: `1px solid ${palette.divider}`,

	'& img': { willChange: 'opacity', transition: transitions.create('opacity') },

	'&.isMobileFormat ': {
		width: `calc((100% - 24px) / 3)`,
	},

	'& > button': {
		opacity: 0,
		transition: transitions.create('opacity'),
	},

	'&:not(.isMobileFormat):hover > button': {
		opacity: 1,
	},
}));

const StyledIcon = styled(IconButton)(({ theme: { palette, shape } }) => ({
	'&.MuiButtonBase-root': {
		position: 'absolute',
		width: '100% !important',
		height: '100% !important',
		backgroundColor: palette.action.hover,
		backdropFilter: 'blur(10px)',
		borderRadius: shape.borderRadius,
	},
}));

const StyledMenu = styled(Menu)(({ theme: { palette } }) => ({
	'& .MuiPaper-root': {
		backgroundColor: 'transparent',
		boxShadow: 'none',
	},
	backdropFilter: 'blur(5px) brightness(0.8)',
	backgroundColor: palette.action.hover,
}));

const StyledImage = styled(Image)(({ theme: { shape, palette } }) => ({
	borderRadius: shape.borderRadius,
	backgroundColor: palette.background.default,
}));

const StyledMenuItem = styled(MenuItem)(({ theme: { shape, shadows, palette } }) => ({
	backgroundColor: palette.background.default,
	'&:hover': {
		backgroundColor: palette.background.default,
	},
	borderRadius: shape.borderRadius,
	boxShadow: shadows[1],
	minWidth: 'max-content',
	maxWidth: '100%',
}));

const DeleteImageMenu = ({
	anchorEl,
	closeMenu,
	handleFormImagesChange,
	imageUrls,
	imageToDeleteIndex,
}: {
	anchorEl?: HTMLElement;
	closeMenu: () => void;
	handleFormImagesChange: () => void;
	imageUrls?: string[];
	imageToDeleteIndex?: number;
}) => {
	const [dimensions, setDimensions] = useState<{ height: number; width: number }>();

	useEffect(() => {
		if (imageToDeleteIndex !== undefined && imageUrls?.[imageToDeleteIndex]) {
			const img = new window.Image();
			img.src = imageUrls[imageToDeleteIndex];
			img.onload = () => {
				setDimensions({ height: img.naturalHeight, width: img.naturalWidth });
			};

			return () => {
				img.onload = null;
			};
		}

		return undefined;
	}, [dimensions, imageToDeleteIndex, imageUrls]);

	return (
		<StyledMenu open={!!anchorEl} onClose={closeMenu} anchorEl={anchorEl}>
			<Stack gap={2} maxHeight="80%" minHeight="80%" maxWidth="80%" minWidth="80%">
				{imageToDeleteIndex !== undefined && imageUrls?.[imageToDeleteIndex] && dimensions && (
					<StyledImage
						src={imageUrls[imageToDeleteIndex]}
						alt="Prévisualisation de l'image déposée"
						height={dimensions.height}
						width={dimensions.width}
					/>
				)}

				<StyledMenuItem
					onClick={() => {
						handleFormImagesChange();
						closeMenu();
					}}
				>
					<ListItemIcon>
						<Icon path={mdiDeleteOutline} />
					</ListItemIcon>
					<ListItemText>Supprimer</ListItemText>
				</StyledMenuItem>
			</Stack>
		</StyledMenu>
	);
};

const UploadedImages = ({
	formImages,
	onFormImagesChange,
}: {
	formImages?: File[];
	onFormImagesChange: UseFormSetValue<SupportFormType>;
}) => {
	const { isMobileFormat } = useFormat();

	const [anchorEl, setAnchorEl] = useState<HTMLElement>();
	const [imageToDeleteIndex, setImageToDeleteIndex] = useState<number>();
	const [imageUrls, setImageUrls] = useState<string[]>();

	const closeMenu = () => {
		setAnchorEl(undefined);
	};

	const UploadedImagesRender = useMemo(
		() => (
			<UploadedImagesWrapper>
				{formImages?.map((image, index) => {
					const imageUrl = imageUrls?.[index];
					const imageKey = `${image.name}-${index}`;

					return (
						<ImageContainer className={clsx({ isMobileFormat })} key={imageKey}>
							{imageUrl && (
								<Image
									alt="Prévisualisation de l'image déposée"
									height="100%"
									width="100%"
									src={imageUrl}
									objectFit="scale-down"
								/>
							)}
							<Tooltip content={`${image.name} - ${formatFileSize(image.size)}`}>
								<StyledIcon
									data-index={index}
									onClick={(event) => {
										if (!isMobileFormat) {
											onFormImagesChange(
												'images',
												formImages?.filter((formImage) => image !== formImage),
											);
										} else {
											setImageToDeleteIndex(index);
											setAnchorEl(event.currentTarget);
										}
									}}
								>
									<Icon path={mdiDeleteOutline} size="small" />
								</StyledIcon>
							</Tooltip>
						</ImageContainer>
					);
				})}
			</UploadedImagesWrapper>
		),
		[formImages, imageUrls, isMobileFormat, onFormImagesChange],
	);

	useEffect(() => {
		const urls = formImages?.map((image) => URL.createObjectURL(image));
		setImageUrls(urls);

		return () => urls?.forEach((url) => URL.revokeObjectURL(url));
	}, [formImages]);

	return (
		<>
			{UploadedImagesRender}
			<DeleteImageMenu
				anchorEl={anchorEl}
				closeMenu={closeMenu}
				imageUrls={imageUrls}
				imageToDeleteIndex={imageToDeleteIndex}
				handleFormImagesChange={() =>
					onFormImagesChange(
						'images',
						formImages?.filter((_, index) => index !== imageToDeleteIndex),
					)
				}
			/>
		</>
	);
};

export default UploadedImages;
